import Cookies from 'js-cookie';
import en from './i18n/en';
import hi from './i18n/hi';
import te from './i18n/te';
import ta from './i18n/ta';
import kn from './i18n/kn';

/**
 * Flip to `true` when multi-language UI is ready to ship.
 * While `false`, the app always uses English and ignores language cookies / browser prefs.
 */
export const isLanguageSelectionEnabled = false;

/** Supported UI languages — English, Hindi, Telugu, Tamil, Kannada. */
export const SUPPORTED_LANGUAGES = {
  en: { name: 'English', nativeName: 'English', code: 'en' },
  hi: { name: 'Hindi', nativeName: 'हिन्दी', code: 'hi' },
  te: { name: 'Telugu', nativeName: 'తెలుగు', code: 'te' },
  ta: { name: 'Tamil', nativeName: 'தமிழ்', code: 'ta' },
  kn: { name: 'Kannada', nativeName: 'ಕನ್ನಡ', code: 'kn' },
} as const;

export type SupportedLanguage = keyof typeof SUPPORTED_LANGUAGES;

const DEFAULT_LANGUAGE: SupportedLanguage = 'en';
const LANGUAGE_COOKIE_NAME = 'chalo_language';
const LANGUAGE_COOKIE_OPTIONS = {
  secure: true,
  sameSite: 'Strict' as const,
  expires: 365,
};

const BROWSER_LANG_CODES = new Set<SupportedLanguage>(['en', 'hi', 'te', 'ta', 'kn']);

function isSupportedLanguage(value: string | undefined): value is SupportedLanguage {
  return !!value && value in SUPPORTED_LANGUAGES;
}

export const languageUtils = {
  isLanguageSelectionEnabled,

  getCurrentLanguage(): SupportedLanguage {
    if (!isLanguageSelectionEnabled) {
      return DEFAULT_LANGUAGE;
    }

    const savedLanguage = Cookies.get(LANGUAGE_COOKIE_NAME);
    if (isSupportedLanguage(savedLanguage)) {
      return savedLanguage;
    }

    const browserLang = navigator.language.split('-')[0];
    if (isSupportedLanguage(browserLang) && BROWSER_LANG_CODES.has(browserLang)) {
      return browserLang;
    }

    return DEFAULT_LANGUAGE;
  },

  setLanguage(language: SupportedLanguage): void {
    if (!isLanguageSelectionEnabled) {
      return;
    }
    if (!isSupportedLanguage(language)) {
      return;
    }
    Cookies.set(LANGUAGE_COOKIE_NAME, language, LANGUAGE_COOKIE_OPTIONS);
    window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language } }));
  },

  getLanguageName(language: SupportedLanguage): string {
    return (
      SUPPORTED_LANGUAGES[language]?.nativeName ||
      SUPPORTED_LANGUAGES[language]?.name ||
      'English'
    );
  },

  getSupportedLanguages() {
    const entries = isLanguageSelectionEnabled
      ? Object.entries(SUPPORTED_LANGUAGES)
      : Object.entries(SUPPORTED_LANGUAGES).filter(([code]) => code === DEFAULT_LANGUAGE);

    return entries.map(([code, lang]) => ({
      code,
      name: lang.name,
      nativeName: lang.nativeName,
    }));
  },
};

export const translations = { en, hi, te, ta, kn };

function getNestedTranslation(obj: any, key: string): any {
  return key.split('.').reduce(
    (acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined),
    obj
  );
}

type TranslationSection =
  | 'getHelp'
  | 'myIssues'
  | 'reportIssue'
  | 'common'
  | 'aboutYou'
  | 'account'
  | 'settings'
  | 'header'
  | 'footer'
  | 'howItWorks'
  | 'safety'
  | 'hero'
  | 'statistics'
  | 'benefits'
  | 'testimonials'
  | 'topBar'
  | 'quickRideFinder'
  | 'appStore';

export const getTranslation = (
  key: string,
  section: TranslationSection = 'common'
): string => {
  const currentLanguage = languageUtils.getCurrentLanguage();
  const translation = getNestedTranslation(translations[currentLanguage]?.[section], key);
  if (!translation) {
    return getNestedTranslation(translations.en[section], key) || key;
  }
  return translation;
};

export const getTranslationArray = (
  key: string,
  section: TranslationSection = 'common'
): string[] => {
  const currentLanguage = languageUtils.getCurrentLanguage();
  const translation = getNestedTranslation(translations[currentLanguage]?.[section], key);
  if (!translation || !Array.isArray(translation)) {
    return getNestedTranslation(translations.en[section], key) || [];
  }
  return translation;
};
